// ORCA360 - Mobile Instructor Recording Workflow
// 8-step wizard: Record Briefing → Record Simulator → Speaker Labels →
// Generate Debrief → Read Debrief → Record Debriefing → Final Transcript → Assessments
//
// Reuses all existing backend endpoints and components (RecordButton, SpeakerRoleAssignmentModal, etc.)

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAssessmentGeneration, GenerationStage, getStageLabel } from '../hooks/useAssessmentGeneration';
import { useProgressiveTranscription } from '../hooks/useProgressiveTranscription';
import MobileHeader from '../components/mobile/MobileHeader';
import MobileStepIndicator, { WORKFLOW_STEPS } from '../components/mobile/MobileStepIndicator';
import MobileBottomBar from '../components/mobile/MobileBottomBar';
import MobileProgressOverlay from '../components/mobile/MobileProgressOverlay';
import MobileSpeakerRoles from '../components/mobile/MobileSpeakerRoles';
import DebriefingMobileView from '../components/mobile/DebriefingMobileView';
import RecordButton from '../components/RecordButton';
import type { SpeakerRoleAssignment } from '../components/SpeakerRoleAssignmentModal';
import type { RecordingPhase } from '../lib/audioUpload';
import { isIOS } from '../utils/device';

// ── Types ────────────────────────────────────────────────────────────────

interface SessionData {
  id: string;
  session_date: string;
  aircraft_type: string;
  flight_phase: string;
  status: string;
  organization_id: string;
  trainee_id: string;
  instructor_id: string;
  trainee?: { full_name: string };
  instructor?: { full_name: string };
  sim_start_time?: string | null;
  sim_end_time?: string | null;
  briefing_start_time?: string | null;
  debrief_start_time?: string | null;
  early_transcription_triggered?: boolean;
  session_timezone?: string | null;
}

interface RecordingRow {
  id: string;
  phase: RecordingPhase | 'briefing_sim' | 'full';
  status: string;
  duration_seconds: number | null;
}

// ── Component ────────────────────────────────────────────────────────────

export default function SessionWorkflow() {
  const { id: sessionId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // ── Session data ────────────────────────────────────────────────────
  const [session, setSession] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Recordings & transcripts state ──────────────────────────────────
  const [recordings, setRecordings] = useState<RecordingRow[]>([]);
  const [hasTranscript, setHasTranscript] = useState(false);
  const [hasSpeakerRoles, setHasSpeakerRoles] = useState(false);
  const [hasDebriefingGuide, setHasDebriefingGuide] = useState(false);
  const [hasAssessments, setHasAssessments] = useState(false);

  // ── Wizard state ────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(0);
  const [showSpeakerModal, setShowSpeakerModal] = useState(false);

  // ── Processing states ───────────────────────────────────────────────
  const [generatingDebrief, setGeneratingDebrief] = useState(false);
  const [debriefProgress, setDebriefProgress] = useState(0);

  // ── Assessment generation ───────────────────────────────────────────
  const assessment = useAssessmentGeneration();

  // ── Progressive transcription ─────────────────────────────────────
  const progressive = useProgressiveTranscription(sessionId);

  // ── T-15 early trigger check (runs during sim recording) ──────────
  useEffect(() => {
    if (!session?.sim_end_time || progressive.earlyTriggered) return;
    // Only run interval during sim recording step
    if (currentStep !== 1) return;

    const checkT15 = () => {
      const simEndMs = new Date(session.sim_end_time!).getTime();
      const nowMs = Date.now();
      const fifteenMinMs = 15 * 60 * 1000;

      if (nowMs >= simEndMs - fifteenMinMs) {
        console.log('[SessionWorkflow] T-15 reached, triggering early transcription');
        progressive.triggerEarlyTranscription();
      }
    };

    // Check immediately
    checkT15();
    // Then every 30 seconds
    const interval = setInterval(checkT15, 30_000);
    return () => clearInterval(interval);
  }, [session?.sim_end_time, currentStep, progressive.earlyTriggered, progressive.triggerEarlyTranscription]);

  // ── Load session & related data ─────────────────────────────────────
  const loadSession = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const { data, error: fetchErr } = await supabase
        .from('sessions')
        .select(`
          id, session_date, aircraft_type, flight_phase, status,
          organization_id, trainee_id, instructor_id,
          sim_start_time, sim_end_time, briefing_start_time, debrief_start_time,
          early_transcription_triggered, session_timezone,
          trainee:trainee_id(full_name),
          instructor:instructor_id(full_name)
        `)
        .eq('id', sessionId)
        .single();

      if (fetchErr) throw fetchErr;

      // Normalize nested relations
      const s = {
        ...data,
        trainee: Array.isArray(data.trainee) ? data.trainee[0] : data.trainee,
        instructor: Array.isArray(data.instructor) ? data.instructor[0] : data.instructor,
      } as SessionData;

      setSession(s);
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const loadRecordings = useCallback(async () => {
    if (!sessionId) return;
    const { data } = await supabase
      .from('session_recordings')
      .select('id, phase, status, duration_seconds')
      .eq('session_id', sessionId)
      .order('created_at');
    setRecordings(data || []);
  }, [sessionId]);

  const loadTranscripts = useCallback(async () => {
    if (!sessionId) return;
    const { count } = await supabase
      .from('transcripts')
      .select('id', { count: 'exact', head: true })
      .eq('session_id', sessionId);
    setHasTranscript((count ?? 0) > 0);
  }, [sessionId]);

  const loadSpeakerRoles = useCallback(async () => {
    if (!sessionId) return;
    const { count } = await supabase
      .from('speaker_role_mappings')
      .select('id', { count: 'exact', head: true })
      .eq('session_id', sessionId);
    setHasSpeakerRoles((count ?? 0) > 0);
  }, [sessionId]);

  const loadDebriefingGuide = useCallback(async () => {
    if (!sessionId) return;
    const { count } = await supabase
      .from('debriefing_guides')
      .select('id', { count: 'exact', head: true })
      .eq('session_id', sessionId);
    setHasDebriefingGuide((count ?? 0) > 0);
  }, [sessionId]);

  const loadAssessments = useCallback(async () => {
    if (!sessionId) return;
    const { count } = await supabase
      .from('assessments_pilot')
      .select('id', { count: 'exact', head: true })
      .eq('session_id', sessionId);
    setHasAssessments((count ?? 0) > 0);
  }, [sessionId]);

  const refreshAll = useCallback(async () => {
    await Promise.all([
      loadSession(),
      loadRecordings(),
      loadTranscripts(),
      loadSpeakerRoles(),
      loadDebriefingGuide(),
      loadAssessments(),
    ]);
  }, [loadSession, loadRecordings, loadTranscripts, loadSpeakerRoles, loadDebriefingGuide, loadAssessments]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // ── Derived state ───────────────────────────────────────────────────

  const hasAnyRecording = recordings.length > 0;
  const hasTranscribedRecording = recordings.some(r => ['transcribed', 'completed'].includes(r.status));
  const hasDebriefingRecording = recordings.some(r => r.phase === 'debriefing');

  // Determine which steps are completed
  // Accept any post-upload status as complete for recording steps
  const DONE_STATUSES = ['uploaded', 'transcribing', 'transcribed', 'completed', 'archived'];
  const isRecordingDone = (phase: RecordingPhase) =>
    recordings.some(r => r.phase === phase && DONE_STATUSES.includes(r.status));

  const completedSteps = useMemo(() => {
    const completed = new Set<number>();
    if (isRecordingDone('briefing')) completed.add(0);
    if (isRecordingDone('simulator')) completed.add(1);
    if (hasSpeakerRoles) completed.add(2);
    if (hasDebriefingGuide) completed.add(3);
    if (hasDebriefingGuide) completed.add(4); // read step auto-completes when guide exists
    if (isRecordingDone('debriefing')) completed.add(5);
    if (hasTranscript && hasSpeakerRoles) completed.add(6); // final transcript ready
    if (hasAssessments) completed.add(7);
    return completed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordings, hasSpeakerRoles, hasDebriefingGuide, hasTranscript, hasAssessments]);

  // Determine which steps are unlocked (accessible)
  const unlockedSteps = useMemo(() => {
    const unlocked = new Set<number>();
    // Steps 0-1: Recording always available
    unlocked.add(0);
    unlocked.add(1);
    // Step 2: Speaker labels - need at least one recording
    if (hasAnyRecording) unlocked.add(2);
    // Step 3: Generate debrief - need transcript + speaker roles
    if (hasTranscript && hasSpeakerRoles) unlocked.add(3);
    // Step 4: Read debrief - need guide
    if (hasDebriefingGuide) unlocked.add(4);
    // Step 5: Record debriefing - after reading debrief (or always available as skip)
    unlocked.add(5);
    // Step 6: Final transcript - need debriefing recording or skip
    if (hasTranscript) unlocked.add(6);
    // Step 7: Assessments - need transcript + speaker roles
    if (hasTranscript && hasSpeakerRoles) unlocked.add(7);
    return unlocked;
  }, [hasAnyRecording, hasTranscript, hasSpeakerRoles, hasDebriefingGuide]);

  // ── Actions ─────────────────────────────────────────────────────────

  // ── Schedule management ────────────────────────────────────────────
  const handleScheduleSave = useCallback(async (schedule: {
    simStart: string;
    simEnd: string;
    briefingStart?: string;
    debriefStart?: string;
  }) => {
    if (!sessionId) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { error: updateErr } = await supabase
      .from('sessions')
      .update({
        sim_start_time: schedule.simStart,
        sim_end_time: schedule.simEnd,
        briefing_start_time: schedule.briefingStart || null,
        debrief_start_time: schedule.debriefStart || null,
        session_timezone: tz,
      })
      .eq('id', sessionId);

    if (updateErr) {
      setError(`Failed to save schedule: ${updateErr.message}`);
    } else {
      await loadSession();
    }
  }, [sessionId, loadSession]);

  const handleRecordingComplete = useCallback(async () => {
    await loadRecordings();
    await loadTranscripts();
  }, [loadRecordings, loadTranscripts]);

  const handleSpeakerRolesComplete = useCallback(async (_assignments: SpeakerRoleAssignment[]) => {
    setShowSpeakerModal(false);
    setHasSpeakerRoles(true);
    await loadSpeakerRoles();
    // Auto-advance to next step
    setCurrentStep(3);
  }, [loadSpeakerRoles]);

  const handleGenerateDebrief = useCallback(async () => {
    if (!sessionId) return;
    setGeneratingDebrief(true);
    setDebriefProgress(0);

    const interval = setInterval(() => {
      setDebriefProgress(prev => (prev < 90 ? prev + 8 : prev));
    }, 2000);

    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const response = await fetch('/api/generate-debriefing-guide', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession?.access_token}`,
        },
        body: JSON.stringify({ sessionId }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to generate debriefing guide');

      setDebriefProgress(100);
      setHasDebriefingGuide(true);
      // Auto-advance to read step
      setCurrentStep(4);
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      clearInterval(interval);
      setGeneratingDebrief(false);
      setDebriefProgress(0);
    }
  }, [sessionId]);

  const handleGenerateAssessments = useCallback(async () => {
    if (!sessionId) return;
    try {
      await assessment.generateAssessments(sessionId);
      setHasAssessments(true);
    } catch {
      // Error is captured in assessment.error
    }
  }, [sessionId, assessment]);

  const handleCombineTranscripts = useCallback(async () => {
    if (!sessionId) return;
    // For now, advance to assessments — combine is optional
    setCurrentStep(7);
  }, [sessionId]);

  // ── Navigation ──────────────────────────────────────────────────────

  const goBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const goNext = () => {
    if (currentStep < WORKFLOW_STEPS.length - 1) setCurrentStep(currentStep + 1);
  };

  const goToSessionDetails = () => {
    navigate(`/sessions/${sessionId}`);
  };

  // ── Rendering ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-navy/30 border-t-navy rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading session...</p>
        </div>
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-red-500 text-sm mb-4">{error}</p>
          <button onClick={() => navigate('/sessions')} className="btn-primary text-sm">
            Back to Sessions
          </button>
        </div>
      </div>
    );
  }

  const sessionTitle = session
    ? `${session.trainee?.full_name || 'Session'} — ${session.aircraft_type || ''}`
    : 'Session Workflow';
  const sessionSubtitle = session
    ? `${session.flight_phase || ''} · ${session.session_date || ''}`
    : '';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header zone — capped at 1/3 viewport */}
      <div className="flex-shrink-0 overflow-hidden" style={{ maxHeight: '33vh' }}>
        {/* Header */}
        <MobileHeader
          title={sessionTitle}
          subtitle={sessionSubtitle}
          onBack={() => navigate(-1)}
          onClose={goToSessionDetails}
        />

        {/* Step Indicator */}
        <div className="bg-white border-b border-slate-100">
          <MobileStepIndicator
            currentStep={currentStep}
            completedSteps={completedSteps}
            unlockedSteps={unlockedSteps}
            onStepTap={(step) => unlockedSteps.has(step) && setCurrentStep(step)}
          />
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto px-4 py-6" style={{ paddingBottom: 100 }}>
        {currentStep === 0 && (
          <RecordingStep
            key="briefing"
            phase="briefing"
            title="Record Briefing"
            description="Record the pre-session briefing. The audio will be automatically transcribed."
            sessionId={sessionId!}
            recording={recordings.find(r => r.phase === 'briefing')}
            onComplete={handleRecordingComplete}
            onError={setError}
          />
        )}

        {currentStep === 1 && (
          <div>
            <RecordingStep
              key="simulator"
              phase="simulator"
              title="Record Simulator Session"
              description="Record the simulator training session. For long sessions (1-2 hrs), consider using a dedicated recording app and uploading via the desktop interface."
              sessionId={sessionId!}
              recording={recordings.find(r => r.phase === 'simulator')}
              onComplete={handleRecordingComplete}
              onError={setError}
              showIOSWarning
            />
            <SimScheduleInput
              simStart={session?.sim_start_time || ''}
              simEnd={session?.sim_end_time || ''}
              briefingStart={session?.briefing_start_time || ''}
              debriefStart={session?.debrief_start_time || ''}
              onSave={handleScheduleSave}
            />
            {/* Progressive transcription status during sim recording */}
            <ProgressiveStatusBanner
              phases={progressive.phases}
              earlyTriggered={progressive.earlyTriggered}
              isProcessing={progressive.isProcessing}
              isComplete={progressive.isComplete}
              simEndTime={session?.sim_end_time || null}
            />
          </div>
        )}

        {currentStep === 2 && (
          <SpeakerLabelsStep
            hasTranscribedRecording={hasTranscribedRecording}
            hasSpeakerRoles={hasSpeakerRoles}
            onAssign={() => setShowSpeakerModal(true)}
          />
        )}

        {currentStep === 3 && (
          <GenerateDebriefStep
            hasDebriefingGuide={hasDebriefingGuide}
            isGenerating={generatingDebrief}
            onGenerate={handleGenerateDebrief}
          />
        )}

        {currentStep === 4 && (
          <div>
            <StepTitle icon="📖" title="Review Debriefing Guide" />
            <p className="text-sm text-slate-500 mb-4">
              Review the AI-generated debriefing guide. Use this to facilitate the debriefing session.
            </p>
            {hasDebriefingGuide ? (
              <DebriefingMobileView sessionId={sessionId!} />
            ) : (
              <EmptyCard message="Generate a debriefing guide first (Step 4)" />
            )}
          </div>
        )}

        {currentStep === 5 && (
          <RecordingStep
            key="debriefing"
            phase="debriefing"
            title="Record Debriefing"
            description="Record the debriefing session with the trainee."
            sessionId={sessionId!}
            recording={recordings.find(r => r.phase === 'debriefing')}
            onComplete={handleRecordingComplete}
            onError={setError}
          />
        )}

        {currentStep === 6 && (
          <FinalTranscriptStep
            hasDebriefingRecording={hasDebriefingRecording}
            hasTranscript={hasTranscript}
            onCombine={handleCombineTranscripts}
          />
        )}

        {currentStep === 7 && (
          <AssessmentsStep
            hasAssessments={hasAssessments}
            isGenerating={assessment.isGenerating}
            progress={assessment.progress}
            error={assessment.error}
            onGenerate={handleGenerateAssessments}
            onViewResults={() => navigate(`/sessions/${sessionId}/edit-pilot-assessment?view=report`)}
          />
        )}
      </div>

      {/* Bottom Bar */}
      <MobileBottomBar
        onBack={currentStep > 0 ? goBack : undefined}
        onNext={currentStep === 7 ? goToSessionDetails : goNext}
        showBack={currentStep > 0}
        showSkip={[0, 1, 5, 6].includes(currentStep)}
        onSkip={goNext}
        nextLabel={currentStep === 7 ? 'Finish' : 'Next'}
      />

      {/* Speaker Role Modal */}
      <MobileSpeakerRoles
        sessionId={sessionId!}
        instructorId={session?.instructor_id}
        isOpen={showSpeakerModal}
        onClose={() => setShowSpeakerModal(false)}
        onComplete={handleSpeakerRolesComplete}
      />

      {/* Progress Overlays */}
      <MobileProgressOverlay
        visible={generatingDebrief}
        title="Generating Debriefing Guide"
        subtitle="Analyzing transcript and building facilitation structure..."
        progress={debriefProgress}
      />
      <MobileProgressOverlay
        visible={assessment.isGenerating}
        title="Generating Assessments"
        subtitle={getStageLabel(assessment.stage)}
        progress={assessment.progress}
        statusText={assessment.stage !== GenerationStage.IDLE ? `Stage: ${getStageLabel(assessment.stage)}` : undefined}
      />

      {/* Error toast */}
      {error && (
        <div className="fixed top-20 left-4 right-4 z-50 p-3 bg-red-50 border border-red-200 rounded-xl shadow-lg">
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-1 text-xs text-red-500 font-semibold"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function StepTitle({ icon, title }: { icon: string; title: string }) {
  return (
    <h2 className="text-lg font-bold text-navy mb-1 flex items-center gap-2">
      <span>{icon}</span> {title}
    </h2>
  );
}

function EmptyCard({ message }: { message: string }) {
  return (
    <div className="p-6 bg-white rounded-xl border border-dashed border-slate-300 text-center">
      <p className="text-sm text-slate-400">{message}</p>
    </div>
  );
}

function StatusBadge({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
        ok ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-yellow-800'
      }`}
    >
      <span className={`w-2 h-2 rounded-full ${ok ? 'bg-green-600' : 'bg-amber-600'}`} />
      {label}
    </span>
  );
}

// ── Recording Step ──────────────────────────────────────────────────────

function RecordingStep({
  phase,
  title,
  description,
  sessionId,
  recording,
  onComplete,
  onError,
  showIOSWarning,
}: {
  phase: RecordingPhase;
  title: string;
  description: string;
  sessionId: string;
  recording?: RecordingRow;
  onComplete: () => void;
  onError: (msg: string) => void;
  showIOSWarning?: boolean;
}) {
  const phaseIcon = phase === 'briefing' ? '📋' : phase === 'simulator' ? '✈️' : '📝';

  return (
    <div>
      <StepTitle icon={phaseIcon} title={title} />
      <p className="text-sm text-slate-500 mb-4">{description}</p>

      {showIOSWarning && isIOS() && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-xs text-amber-700">
            <strong>iOS note:</strong> Keep the screen on during recording. iOS may stop recording if you switch apps or lock the screen.
          </p>
        </div>
      )}

      {/* Existing recording status */}
      {recording && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
          <span className="text-green-600 text-lg">✓</span>
          <div>
            <p className="text-sm font-semibold text-green-800">
              {phase.charAt(0).toUpperCase() + phase.slice(1)} recorded
            </p>
            <p className="text-xs text-green-600">
              Status: {recording.status}
              {recording.duration_seconds
                ? ` · ${Math.floor(recording.duration_seconds / 60)}m ${recording.duration_seconds % 60}s`
                : ''}
            </p>
          </div>
        </div>
      )}

      {/* RecordButton — handles the full record → upload → transcribe cycle */}
      <div className="flex flex-col items-center py-6">
        <RecordButton
          sessionId={sessionId}
          defaultPhase={phase}
          onRecordingComplete={onComplete}
          onError={onError}
        />
      </div>
    </div>
  );
}

// ── Speaker Labels Step ─────────────────────────────────────────────────

function SpeakerLabelsStep({
  hasTranscribedRecording,
  hasSpeakerRoles,
  onAssign,
}: {
  hasTranscribedRecording: boolean;
  hasSpeakerRoles: boolean;
  onAssign: () => void;
}) {
  return (
    <div>
      <StepTitle icon="🎤" title="Speaker Labels" />
      <p className="text-sm text-slate-500 mb-4">
        Verify who is speaking in the recordings. AssemblyAI auto-detects roles, but you can review and correct them.
      </p>

      <div className="flex flex-wrap gap-2 mb-5">
        <StatusBadge label={hasTranscribedRecording ? 'Transcript ready' : 'Awaiting transcript'} ok={hasTranscribedRecording} />
        <StatusBadge label={hasSpeakerRoles ? 'Roles assigned' : 'Roles needed'} ok={hasSpeakerRoles} />
      </div>

      {!hasTranscribedRecording ? (
        <EmptyCard message="Waiting for at least one recording to be transcribed..." />
      ) : (
        <button
          onClick={onAssign}
          className="btn-mobile bg-navy text-white hover:bg-navy/90 transition-colors"
        >
          {hasSpeakerRoles ? 'Re-assign Speaker Roles' : 'Assign Speaker Roles'}
        </button>
      )}
    </div>
  );
}

// ── Generate Debrief Step ───────────────────────────────────────────────

function GenerateDebriefStep({
  hasDebriefingGuide,
  isGenerating,
  onGenerate,
}: {
  hasDebriefingGuide: boolean;
  isGenerating: boolean;
  onGenerate: () => void;
}) {
  return (
    <div>
      <StepTitle icon="⚡" title="Generate Debriefing Guide" />
      <p className="text-sm text-slate-500 mb-4">
        AI generates a structured debriefing guide with facilitator questions, event analysis, and crew strategy prompts.
      </p>

      {hasDebriefingGuide ? (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-center">
          <p className="text-sm font-semibold text-green-800">Debriefing guide ready</p>
          <p className="text-xs text-green-600 mt-1">Proceed to the next step to review it.</p>
        </div>
      ) : (
        <button
          onClick={onGenerate}
          disabled={isGenerating}
          className="btn-mobile bg-amber-500 text-white hover:bg-amber-600 disabled:bg-amber-400 transition-colors"
        >
          {isGenerating ? 'Generating...' : 'Generate Debriefing Guide'}
        </button>
      )}
    </div>
  );
}

// ── Final Transcript Step ───────────────────────────────────────────────

function FinalTranscriptStep({
  hasDebriefingRecording,
  hasTranscript,
  onCombine,
}: {
  hasDebriefingRecording: boolean;
  hasTranscript: boolean;
  onCombine: () => void;
}) {
  return (
    <div>
      <StepTitle icon="🔗" title="Final Transcription" />
      <p className="text-sm text-slate-500 mb-4">
        Optionally combine all recordings into a final transcript. You can skip this step if individual transcripts are sufficient.
      </p>

      <div className="flex flex-wrap gap-2 mb-5">
        <StatusBadge label={hasTranscript ? 'Transcript available' : 'No transcript'} ok={hasTranscript} />
        <StatusBadge label={hasDebriefingRecording ? 'Debriefing recorded' : 'No debriefing'} ok={hasDebriefingRecording} />
      </div>

      <button
        onClick={onCombine}
        className="btn-mobile bg-navy text-white hover:bg-navy/90 transition-colors"
      >
        Continue to Assessments
      </button>
    </div>
  );
}

// ── Assessments Step ────────────────────────────────────────────────────

function AssessmentsStep({
  hasAssessments,
  isGenerating,
  progress,
  error,
  onGenerate,
  onViewResults,
}: {
  hasAssessments: boolean;
  isGenerating: boolean;
  progress: number;
  error: string | null;
  onGenerate: () => void;
  onViewResults: () => void;
}) {
  return (
    <div>
      <StepTitle icon="📊" title="Generate Assessments" />
      <p className="text-sm text-slate-500 mb-4">
        AI generates pilot competency assessments and instructor effectiveness evaluations with evidence validation.
      </p>

      {hasAssessments ? (
        <div className="space-y-3">
          <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-center">
            <p className="text-sm font-semibold text-green-800">Assessments generated</p>
            <p className="text-xs text-green-600 mt-1">
              View detailed results on the session details page.
            </p>
          </div>
          <button
            onClick={onViewResults}
            className="btn-mobile bg-navy text-white hover:bg-navy/90 transition-colors"
          >
            View Full Results
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <button
            onClick={onGenerate}
            disabled={isGenerating}
            className="btn-mobile bg-orca-elevate text-white hover:bg-orca-elevate/90 disabled:bg-slate-400 transition-colors"
          >
            {isGenerating ? `Generating... ${Math.round(progress)}%` : 'Generate Assessments'}
          </button>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sim Schedule Input ──────────────────────────────────────────────────

function SimScheduleInput({
  simStart,
  simEnd,
  briefingStart,
  debriefStart,
  onSave,
}: {
  simStart: string;
  simEnd: string;
  briefingStart: string;
  debriefStart: string;
  onSave: (schedule: {
    simStart: string;
    simEnd: string;
    briefingStart?: string;
    debriefStart?: string;
  }) => void;
}) {
  const [localSimStart, setLocalSimStart] = useState(
    simStart ? toLocalDatetime(simStart) : ''
  );
  const [localSimEnd, setLocalSimEnd] = useState(
    simEnd ? toLocalDatetime(simEnd) : ''
  );
  const [localBriefingStart, setLocalBriefingStart] = useState(
    briefingStart ? toLocalDatetime(briefingStart) : ''
  );
  const [localDebriefStart, setLocalDebriefStart] = useState(
    debriefStart ? toLocalDatetime(debriefStart) : ''
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saved, setSaved] = useState(!!simStart && !!simEnd);

  const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Calculate T-15 time
  const tMinus15 = localSimEnd
    ? new Date(new Date(localSimEnd).getTime() - 15 * 60 * 1000)
    : null;

  const handleSave = () => {
    setValidationError(null);

    if (!localSimStart || !localSimEnd) {
      setValidationError('Sim start and end times are required');
      return;
    }

    const start = new Date(localSimStart);
    const end = new Date(localSimEnd);
    const durationMin = (end.getTime() - start.getTime()) / 60000;

    if (end <= start) {
      setValidationError('End time must be after start time');
      return;
    }
    if (durationMin < 30) {
      setValidationError('Session must be at least 30 minutes');
      return;
    }
    if (durationMin > 480) {
      setValidationError('Session cannot exceed 8 hours');
      return;
    }

    onSave({
      simStart: new Date(localSimStart).toISOString(),
      simEnd: new Date(localSimEnd).toISOString(),
      briefingStart: localBriefingStart
        ? new Date(localBriefingStart).toISOString()
        : undefined,
      debriefStart: localDebriefStart
        ? new Date(localDebriefStart).toISOString()
        : undefined,
    });
    setSaved(true);
  };

  return (
    <div className="mt-6 p-4 bg-white rounded-xl border border-slate-200">
      <h3 className="text-sm font-bold text-navy mb-1">Session Schedule</h3>
      <p className="text-xs text-slate-500 mb-3">
        Set sim times to enable early transcription of the briefing at T-15 minutes.
      </p>

      <div className="space-y-3">
        {/* Sim start */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Sim start *
          </label>
          <input
            type="datetime-local"
            value={localSimStart}
            onChange={(e) => { setLocalSimStart(e.target.value); setSaved(false); }}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-navy/20 focus:border-navy"
          />
        </div>

        {/* Sim end */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Sim end *
          </label>
          <input
            type="datetime-local"
            value={localSimEnd}
            onChange={(e) => { setLocalSimEnd(e.target.value); setSaved(false); }}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-navy/20 focus:border-navy"
          />
        </div>

        {/* Briefing start (optional) */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Briefing start <span className="text-slate-400">(optional)</span>
          </label>
          <input
            type="datetime-local"
            value={localBriefingStart}
            onChange={(e) => { setLocalBriefingStart(e.target.value); setSaved(false); }}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-navy/20 focus:border-navy"
          />
        </div>

        {/* Debrief start (optional) */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Debrief start <span className="text-slate-400">(optional)</span>
          </label>
          <input
            type="datetime-local"
            value={localDebriefStart}
            onChange={(e) => { setLocalDebriefStart(e.target.value); setSaved(false); }}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-navy/20 focus:border-navy"
          />
        </div>

        {/* Timezone */}
        <p className="text-xs text-slate-400">
          Timezone: {detectedTz}
        </p>

        {/* T-15 indicator */}
        {tMinus15 && (
          <div className="p-2 bg-sky-50 border border-sky-200 rounded-lg">
            <p className="text-xs text-sky-700">
              Early transcription will start at{' '}
              <strong>
                {tMinus15.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </strong>{' '}
              (T-15 minutes)
            </p>
          </div>
        )}

        {/* Validation error */}
        {validationError && (
          <p className="text-xs text-red-600">{validationError}</p>
        )}

        {/* Save button */}
        <button
          onClick={handleSave}
          disabled={saved}
          className={`w-full py-2 text-sm font-semibold rounded-lg transition-colors ${
            saved
              ? 'bg-green-100 text-green-700 cursor-default'
              : 'bg-navy text-white hover:bg-navy/90'
          }`}
        >
          {saved ? 'Schedule saved' : 'Save Schedule'}
        </button>
      </div>
    </div>
  );
}

/** Convert an ISO string to a datetime-local input value */
function toLocalDatetime(isoStr: string): string {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ── Progressive Status Banner ──────────────────────────────────────────

const PHASE_LABELS: Record<string, string> = {
  briefing: 'Briefing',
  simulator_main: 'Sim (main)',
  simulator_tail: 'Sim (tail)',
  simulator_stitched: 'Sim (stitched)',
  debriefing: 'Debriefing',
};

const PHASE_STATUS_ICON: Record<string, string> = {
  idle: '',
  pending: '...',
  processing: '...',
  completed: '',
  failed: '',
  stitching: '...',
};

function ProgressiveStatusBanner({
  phases,
  earlyTriggered,
  isProcessing,
  isComplete,
  simEndTime,
}: {
  phases: Record<string, string>;
  earlyTriggered: boolean;
  isProcessing: boolean;
  isComplete: boolean;
  simEndTime: string | null;
}) {
  // Don't render if no schedule is set
  if (!simEndTime) return null;

  // Calculate countdown to T-15
  const simEndMs = new Date(simEndTime).getTime();
  const nowMs = Date.now();
  const t15Ms = simEndMs - 15 * 60 * 1000;
  const msUntilT15 = t15Ms - nowMs;

  const hasAnyActivity = earlyTriggered || Object.values(phases).some(s => s !== 'idle');

  if (!hasAnyActivity && msUntilT15 > 0) {
    // Show countdown
    const minsLeft = Math.ceil(msUntilT15 / 60000);
    return (
      <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-xl">
        <p className="text-xs text-slate-500 text-center">
          Early transcription starts in <strong>{minsLeft} min</strong>
        </p>
      </div>
    );
  }

  if (!hasAnyActivity) return null;

  // Show phase statuses
  const activePhases = Object.entries(phases).filter(
    ([, status]) => status !== 'idle'
  );

  return (
    <div className="mt-4 p-3 bg-sky-50 border border-sky-200 rounded-xl">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold text-navy">Progressive Transcription</h4>
        {isComplete && (
          <span className="text-xs text-green-700 font-semibold">Ready</span>
        )}
        {isProcessing && (
          <span className="flex items-center gap-1 text-xs text-sky-700">
            <span className="inline-block w-2 h-2 bg-sky-500 rounded-full animate-pulse" />
            Processing
          </span>
        )}
      </div>

      {activePhases.length > 0 && (
        <div className="space-y-1">
          {activePhases.map(([phase, status]) => (
            <div key={phase} className="flex items-center justify-between text-xs">
              <span className="text-slate-600">
                {PHASE_STATUS_ICON[status] || ''}{' '}
                {PHASE_LABELS[phase] || phase}
              </span>
              <span
                className={
                  status === 'completed'
                    ? 'text-green-700 font-medium'
                    : status === 'failed'
                    ? 'text-red-600 font-medium'
                    : status === 'processing' || status === 'stitching'
                    ? 'text-sky-700'
                    : 'text-slate-500'
                }
              >
                {status === 'completed'
                  ? 'Done'
                  : status === 'failed'
                  ? 'Failed'
                  : status === 'processing'
                  ? 'Transcribing...'
                  : status === 'stitching'
                  ? 'Stitching...'
                  : status === 'pending'
                  ? 'Queued'
                  : status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
